import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomSideBar from "../../shadcn/components/custom/CustomSideBar";
import { useRouter } from "next/router";
import HomeIcon from "../../icons/HomeIcon";
import LoginSvg from "../../icons/LoginSvg";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../../shadcn/components/ui/sidebar";
import LogoSvg from "../../icons/Logo";
import UserIcon from "../../icons/UserIcon";
import { AuctionEvents, Archives, Teams, Calendar } from "app-v2";
import { useSelector, useDispatch } from "react-redux";
import {
  setCurrentBranch,
  clearBankerAuth,
  setBankerHasSwissEvents,
} from "store";
import {
  getTeams,
  getBankBranchesByid,
  getNotificationsForBanker,
  getSwissAuctionEvents,
} from "services";
import NotificationIcon from "../../icons/NotificationIcon";
import { Dropdown } from "../../shadcn/components/custom/Dropdown";

interface BankerDashboardLayoutV2Props {
  children: React.ReactNode;
}

interface LeftItemProps {
  getBankBranchesStatic?: any;
  hanldeBranchChange?: any;
  currentBranch?: any;
}

export default function BankerDashboardLayoutV2({
  children,
}: BankerDashboardLayoutV2Props) {
  const router = useRouter();
  const dispatch = useDispatch();
  const [currentActiveTab, setCurrentActiveTab] = useState<any>();
  const [requestsData, setRequestsData] = useState<any>();
  const bankerHasSwissEvents = useSelector(
    (state: any) => state.bankerAuth?.bankerHasSwissEvents ?? false,
  );
  const userAttributes = useSelector((state: any) => {
    return state.bankerAuth?.user?.attributes;
  });
  const userProfile = useSelector((state: any) => {
    return state.bankerAuth.user;
  });

  const bankLogo = useSelector((state: any) => {
    return state.bankerAuth.bank?.logo?.key_path;
  });

  const currentBranch = useSelector((state: any) => {
    return state.bankerAuth.currentBranch;
  });

  const swissCheckKeyRef = useRef<string | null>(null);

  const fetchRequestsData = useCallback(async () => {
    try {
      const swissCheckKey = `${currentBranch?.value ?? "no-branch"}:${
        userProfile?.uuid ?? "no-user"
      }`;
      if (swissCheckKey === swissCheckKeyRef.current) {
        // Skip repeated swiss check for same branch/user combo.
        const res: any = await getNotificationsForBanker({
          branch_uuid: currentBranch?.value,
          status: ["pending"],
          type: "request-count",
          approver_uuid: userProfile?.uuid,
        });

        if (res && res?.[0]) {
          setRequestsData(res?.[0]?.pendingCount);
        } else setRequestsData([]);
        return;
      }
      swissCheckKeyRef.current = swissCheckKey;

      const data: any = await getSwissAuctionEvents(false, 1, true);
      const swissEvents = Array.isArray(data) ? data : data?.data;
      dispatch(
        setBankerHasSwissEvents(
          Array.isArray(swissEvents) && swissEvents.length > 0,
        ),
      );
      const res: any = await getNotificationsForBanker({
        branch_uuid: currentBranch?.value,
        status: ["pending"],
        type: "request-count",
        approver_uuid: userProfile?.uuid,
      });

      if (res && res?.[0]) {
        setRequestsData(res?.[0]?.pendingCount);
        // setRefreshPage(false);
      } else setRequestsData([]);
    } catch (err) {
      // setRefreshPage(false);
      console.log("errors here", err);
    }
  }, [currentBranch?.value, userProfile?.uuid, dispatch]);

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        tabId: "home_page",
        text: "Homepage",
        href: "/",
        icon: <HomeIcon className="w-6 h-6" />,
      },
      {
        tabId: "bid_requests",
        text: requestsData > 0 ? `Requests (${requestsData})` : "Requests",
        href: "/dashboard/bid-requests",
        alertTab: requestsData > 0,
        icon: <NotificationIcon className="w-6 h-6" />,
      },
      {
        tabId: "auction_events",
        text: "Auction Events",
        href: "/dashboard/events",
        icon: <AuctionEvents className="w-6 h-6" />,
      },
      {
        tabId: "archives",
        text: "Archives",
        href: "/dashboard/archives",
        icon: <Archives className="w-5 h-6" />,
      },
      {
        tabId: "teams",
        text: "Teams",
        href: "/dashboard/teams",
        icon: <Teams className="w-6 h-6" />,
      },
      {
        tabId: "event_calendar",
        text: "Event Calendar",
        href: "/dashboard/event-calendar",
        icon: <Calendar className="w-6 h-6" />,
      },
    ];

    if (bankerHasSwissEvents) {
      const swissTab = {
        tabId: "asset_transfer_events",
        text: "Asset transfer Events",
        href: "/dashboard/asset-transfer-events",
        icon: <AuctionEvents className="w-6 h-6" />,
      };
      const auctionIndex = baseTabs.findIndex(
        (tab) => tab.tabId === "auction_events",
      );
      if (auctionIndex >= 0) {
        baseTabs.splice(auctionIndex + 1, 0, swissTab);
      } else {
        baseTabs.push(swissTab);
      }
    }

    return baseTabs;
  }, [requestsData, bankerHasSwissEvents]);

  const tabClickHandler = (tab: any) => {
    setCurrentActiveTab(tab?.tabId);
    router.push(tab.href, undefined, { shallow: true });
    // Add additional logic here as needed
  };

  const logoutHandler = () => {
    dispatch(clearBankerAuth());
    router.push("/login");
  };

  useEffect(() => {
    const tabFound = tabs.find((obj: any) => obj.href === router.pathname);
    if (tabFound) {
      setCurrentActiveTab(tabFound?.tabId);
      fetchRequestsData();
      return;
    }

    if (router.pathname.startsWith("/dashboard/events")) {
      const activeTab = router.pathname.startsWith("/dashboard/swiss-events")
        ? "swiss_events"
        : "auction_events";
      setCurrentActiveTab(activeTab);
      fetchRequestsData();
    }
  }, [router.pathname, tabs, fetchRequestsData]);

  useEffect(() => {
    fetchRequestsData();
  }, [currentBranch, fetchRequestsData]);

  // Left Item data fetching
  const [teamData, setTeamData] = useState<any>();
  const { for_branch } = router.query;
  const hanldeBranchChange = useCallback(
    (value: any) => {
      dispatch(setCurrentBranch(value));
    },
    [dispatch],
  );

  useEffect(() => {
    (async () => {
      const bdata: any = await getBankBranches();

      if (for_branch) {
        const filteredData = bdata.filter(
          (item: any) => item.value === for_branch,
        );
        hanldeBranchChange(filteredData[0]);
      }
    })();
  }, [router, for_branch, hanldeBranchChange]);

  const getBankBranches = async () => {
    const data: any = await getTeams();
    const branches: any = await Promise.all(
      data.map(async (item: any) => {
        if (item["teams*tenant"] === "bank-branch") {
          const branchIdData: any = await getBankBranchesByid([
            item["teams*name"],
          ]);
          if (branchIdData.length > 0) {
            const branchData = branchIdData[0];
            return { ...branchData, ...{ team: item } };
          }
        }
      }),
    );
    const filteredBranches: any = branches.filter((item: any) => item && item);
    const filteredData =
      filteredBranches.length > 0 &&
      filteredBranches.map((item: any) => {
        return { value: item.uuid, label: item.name, team: item.team };
      });
    setTeamData(filteredData);
    return filteredData;
  };

  const getBankBranchesStatic = async () => teamData;

  return (
    <div className="max-h-screen max-w-full bg-white dark:bg-gray-900">
      <SidebarProvider>
        <CustomSideBar
          tabs={tabs}
          currentActiveTabId={currentActiveTab}
          tabOnClickHandler={tabClickHandler}
          logo={<LogoSvg />}
          footer={
            <div
              className="flex items-center text-sm font-openSans gap-1 cursor-pointer"
              onClick={logoutHandler}
            >
              <LoginSvg height="40" width="40" bg_color="bg-transparent" />
              <div className="text-brand-textGrey hover:text-brand-textBlack">
                Logout
              </div>
            </div>
          }
        />
        <SidebarInset className="bg-[#F8F8F8]">
          <header className="h-[80px] flex items-center py-6 shrink-0 gap-2 ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 bg-white border-b-[1px] border-l-[1px] border-[#EEE]">
            <div className="w-full flex items-center gap-2 px-4 lg:px-8">
              <SidebarTrigger className="-ml-1 lg:hidden" />
              <div className="pl-4 md:hidden flex justify-between w-full">
                <LogoSvg />
                <div className="flex items-center gap-2">
                  {/* <button className="bg-white flex gap-3 items-center border-2 hover:bg-gray-100 hover:cursor-pointer border-black rounded-[12px] px-4 xl:px-8 py-2">
                    <DownloadArrow /> Export
                  </button> */}
                  <div className="p-2 rounded-full bg-[#eee]">
                    <UserIcon width={19} height={19} />
                  </div>{" "}
                  <div className="font-openSans text-xs">
                    <div className="font-openSans text-brand-textBlack tracking-wider">
                      {userProfile?.profile?.bio?.full_name}
                    </div>
                    <div className="text-brand-borderGrey text-xs">
                      {userAttributes && userAttributes[0].value}
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full hidden md:flex gap-2 space-between">
                <div className="w-1/2 xl:w-2/3 flex gap-2 md:justify-between">
                  <LeftItem
                    getBankBranchesStatic={getBankBranchesStatic}
                    hanldeBranchChange={hanldeBranchChange}
                    currentBranch={currentBranch}
                  />
                </div>
                <div className="font-openSans text-brand-textBlack text-lg capitalize tracking-wider"></div>
                <div className="w-1/2 xl:w-1/3 flex gap-4 justify-between items-center pl-2">
                  {bankLogo && (
                    <img
                      alt="header"
                      className="object-contain w-[150px] h-auto md:[200px] my-auto "
                      width="310"
                      height=""
                      src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${bankLogo}`}
                    />
                  )}

                  <div className="border w-[1px] h-full"></div>
                  <div className="flex gap-2">
                    <div className="p-2 rounded-full bg-[#eee]">
                      <UserIcon width={19} height={19} />
                    </div>
                    <div className="font-openSans text-xs">
                      <div className="font-openSans text-brand-textBlack tracking-wider">
                        {userProfile?.profile?.bio?.full_name}
                      </div>
                      <div className="text-brand-borderGrey text-xs">
                        {userAttributes && userAttributes[0].value}
                      </div>
                    </div>
                  </div>

                  {/* <button className="bg-white w-[125px] hidden md:flex gap-3 items-center justify-center border-2 hover:bg-gray-100 hover:cursor-pointer border-black rounded-[12px] px-4 py-2">
                    <DownloadArrow /> Export
                  </button> */}
                </div>
              </div>
            </div>
          </header>
          <div className="flex flex-1 flex-col gap-4 bg-white overflow-scroll">
            <div className="flex px-4 lg:px-8 pt-4 md:hidden justify-between items-center">
              <LeftItem
                getBankBranchesStatic={getBankBranchesStatic}
                hanldeBranchChange={hanldeBranchChange}
                currentBranch={currentBranch}
              />
              {bankLogo && (
                <img
                  alt="header"
                  className="object-contain w-[150px] h-auto md:w-[200px] my-auto xl:w-[220px]"
                  width="310"
                  height=""
                  src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${bankLogo}`}
                />
              )}
            </div>
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}

function LeftItem({
  getBankBranchesStatic,
  hanldeBranchChange,
  currentBranch,
}: LeftItemProps) {
  return (
    <>
      <div className="flex flex-row items-center rounded">
        <div className="w-full relative">
          <Dropdown
            dataSource={async () => {
              return await getBankBranchesStatic();
            }}
            defaultSelected={currentBranch ? currentBranch : {}}
            selectIdentifier={true}
            onChange={(value: any) => hanldeBranchChange(value)}
            valueKey="value"
            placeholder={"Bank Branch"}
            styles={{
              dropdownItemsClass:
                "bg-white text-brand-borderGrey max-w-[150px] sm:max-w-[236px] w-full",
              hoverBgColor: "focus:bg-brand-lightOrange",
              hoverTextColor: "focus:text-brand-borderGrey",
              hideDot: true,
              outerContainer: `border-[1px] max-w-[150px] sm:max-w-[236px] sm:min-w-[130px] px-1 sm:px-3 bg-white   w-full rounded-md font-openSans font-normal text-web-body-sm  mid:text-web-body-lg ${
                currentBranch ? "items-end text-black" : "text-black"
              } leading-5 `,
            }}
          />
        </div>
      </div>
    </>
  );
}
